import { QuickAddButton } from "@app/components/ProductCard/QuickAddButton"
import { SizePicker } from "@app/components/ProductCard/SizePicker"
import { VariantPicker } from "@app/components/ProductCard/VariantPicker"
import { addToCartWithZCodes } from "@app/lib/cart"
import { Product, ProductInventory } from "@app/types/product"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { getProductInventoryByStyleColourCode } from "@app/lib/product"
import { HAS_Z_CODE_PRODUCT_TAG } from "@app/utils/z-codes"

interface ProductCardProps {
  product: Product;
  customBadges?: {
    color: string;
    text: string;
  }[];
  fixedWidth?: boolean;
  itemRef?: (node?: Element | null) => void;
}

export const ProductCard = ({ product, customBadges, fixedWidth, itemRef }: ProductCardProps) => {
  const [quickAddActive, setQuickAddActive] = useState(false);
  const [sizeMap, setSizeMap] = useState<{ [key: string]: string }>({})
  const [zProduct, setZProduct] = useState<ProductInventory | null>(null)
  const [loading, setLoading] = useState(false);

  if (!product) return null;

  const isSoldOut = useMemo(() => {
    if (!product.availableForSale) return true;
    if (!product.variants?.edges) return false;
    return product.variants.edges.every((edge) => !edge.node.availableForSale);
  }, [product.availableForSale, product.variants]);

  const isEssential = useMemo(() => product.tags.includes("essential"), [product.tags]);
  const isPreOrder = useMemo(() => product.tags.includes("pre-order"), [product.tags]);
  // Lazy href: compute at click time, not at memo time. This avoids race conditions where
  // <collection-page> may not yet be queryable when ProductCard first mounts.
  const buildHref = () => {
    if (typeof window !== "undefined") {
      const $collectionPage = document.querySelector("collection-page[data-collection-handle]");
      const handleFromAttr = $collectionPage?.getAttribute("data-collection-handle");
      if (handleFromAttr) {
        return `/collections/${handleFromAttr}/products/${product.handle}`;
      }
      const match = window.location.pathname.match(/^\/collections\/([^/]+)/);
      if (match) {
        return `/collections/${match[1]}/products/${product.handle}`;
      }
    }
    return `/products/${product.handle}`;
  };
  // Initial href for SEO/right-click — recomputed on real click via handleClick below.
  const href = buildHref();
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Re-evaluate href at click time (DOM definitely settled by now) and navigate manually
    // so right-click/middle-click still get the freshest URL via href fallback.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return; // let browser handle modifier clicks
    const freshHref = buildHref();
    if (freshHref !== href) {
      e.preventDefault();
      window.location.href = freshHref;
    }
  };
  const price = useMemo(() => product.priceRange.maxVariantPrice.amount, [product.priceRange.maxVariantPrice.amount]);
  const comparedAtPrice = useMemo(
    () => product.compareAtPriceRange.maxVariantPrice.amount,
    [product.compareAtPriceRange.maxVariantPrice.amount],
  );
  const discountPercentage = useMemo(() => {
    const priceN = Number(price);
    const comparedAtPriceN = Number(comparedAtPrice);
    if (priceN === 0) return 0;
    if (comparedAtPrice === price) return 0;
    if (comparedAtPrice < price) return 0;
    return Math.round(((comparedAtPriceN - priceN) / comparedAtPriceN) * 100);
  }, [comparedAtPrice, price]);

  const handleQuickAddClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setQuickAddActive(true);
  }, []);

  const handleQuickAddDismiss = useCallback(() => {
    setQuickAddActive(false);
  }, []);

  const handleAddToCart = useCallback(
    async (
      variantId: string | undefined,
      zVariantId: string | undefined,
      size: string | undefined,
      zSize: string | undefined,
      zQuantity: number
    ) => {
      if (!variantId) {
        return
      }

      setLoading(true);

      await addToCartWithZCodes(1, variantId, size, null, zVariantId, zSize, zQuantity)

      setLoading(false);
      handleQuickAddDismiss();
    },
    [handleQuickAddDismiss],
  );

  const getMappedSize = useCallback((currentSizeValue: string, zProduct: ProductInventory) => {
    if (!zProduct.variants?.nodes) {
      return null
    }

    // Infer direct 1:1 mapping if mapping not explicitly defined
    const mappedSizeValue = sizeMap?.[currentSizeValue] || currentSizeValue

    return zProduct.variants?.nodes.find(node =>
      node.selectedOptions.find(option => option.name === "Size")?.value === mappedSizeValue
    )
  }, [sizeMap])

  const sizes = useMemo(() => {
    if (!product.variants) return [];
    return product.variants.edges.map((v) => {
      const variant = v.node;
      const value = variant.selectedOptions.find((o) => o.name.toLocaleLowerCase() === "size")?.value;

      const mappedSize = value && zProduct ? getMappedSize(value, zProduct) : null
      const zValue = mappedSize?.selectedOptions?.find((o) => o.name.toLocaleLowerCase() === "size")?.value
      const quantity = variant.quantityAvailable || 0
      const zQuantity = mappedSize?.quantityAvailable || 0

      return {
        onClick: () => handleAddToCart(variant?.id?.split("/")?.pop(), mappedSize?.id?.split("/")?.pop(), value, zValue, zQuantity),
        value: value || "",
        disabled: loading || !value || quantity + zQuantity <= 0,
      };
    });
  }, [product.variants, handleAddToCart, loading, zProduct, sizeMap]);

  const initSizeMap = useCallback(() => {
    const map: { [key: string]: string } = {}

    for (const tag of product.tags) {
      if (tag.startsWith("x-z-code-size:")) {
        const split = tag.split(":")
        if (split.length === 3) {
          map[split[1]] = split[2]
        }
      }
    }

    setSizeMap(map)
  }, [product?.tags])

  const fetchZCodeProducts = useCallback(async (zStyleCode: string, zColourCode: string) => {
    const zProduct = await getProductInventoryByStyleColourCode(zStyleCode, zColourCode)
    setZProduct(zProduct)
  }, [])

  useEffect(() => {
    if (product.tags.includes(HAS_Z_CODE_PRODUCT_TAG)) {
      const zStyleCode = product.tags.find(tag => tag.startsWith("x-z-productCode:"))?.split(":")?.[1]
      const zColourCode = product.tags.find(tag => tag.startsWith("x-z-colourCode:"))?.split(":")?.[1]

      if (zStyleCode && zColourCode) {
        initSizeMap()
        fetchZCodeProducts(zStyleCode, zColourCode)
      }
    }
  }, [])

  return (
    <div ref={itemRef} className={fixedWidth ? "w-[168px] md:w-[288px] lg:w-[322px] xl:w-[432px]" : ""}>
      <a
        className={`block relative p-2 md:p-4 bg-brand-cotton rounded-lg xl:rounded-2xl overflow-hidden cursor-pointer group ${quickAddActive ? "is-active" : ""}`}
        href={href}
        onClick={handleClick}
      >
        <div className="absolute top-2 left-2 md:top-4 md:left-4 space-y-0.5 md:space-y-1">
          {isSoldOut ? (
            <div className="text-[0]">
              <badge-custom color="grey" text="SOLD OUT" />
            </div>
          ) : null}
          {isEssential && !isSoldOut && (
            <div className="text-[0]">
              <badge-essential />
            </div>
          )}
          {isPreOrder && !isSoldOut ? (
            <div className="text-[0]">
              <badge-custom color="blue" text="PRE-ORDER" />
            </div>
          ) : null}
          {discountPercentage !== 0 ? (
            <div className="text-[0]">
              <badge-custom color="red" text={`${discountPercentage}% OFF`} />
            </div>
          ) : null}
          {customBadges?.length ? (
            <>
              {customBadges.map((badge, i) => (
                <div key={i} className="text-[0]">
                  <badge-custom color={badge.color} text={badge.text} />
                </div>
              ))}
            </>
          ) : null}
        </div>

        <div className="p-4">
          <img
            className="w-full h-full object-cover object-center mix-blend-darken"
            src={product.images.edges?.[0]?.node.url}
          />
        </div>

        {sizes.length > 0 && !isSoldOut && (
          <>
            <div className="lg:hidden absolute bottom-3 right-3 md:bottom-2 md:right-2">
              <QuickAddButton onClick={handleQuickAddClick} />
            </div>
            <div className="absolute bottom-[-120px] left-0 right-0 lg:group-hover:bottom-0 transition-all">
              <SizePicker isActive={quickAddActive} onDismiss={handleQuickAddDismiss} sizes={sizes} />
            </div>
          </>
        )}
      </a>

      <div className="px-2 lg:px-4 py-4">
        <a className="block mb-2 text-base lg:text-lg text-brand-bistre cursor-pointer" href={href} onClick={handleClick}>
          {product.title}
        </a>
        <div className="mb-4">
          <VariantPicker
            variants={[
              { color: "#2944CC", onClick: () => console.log("clicked"), active: true },
              { color: "#9747FF", onClick: () => console.log("clicked") },
              { color: "#FA6E39", onClick: () => console.log("clicked") },
              { color: "#1BB753", onClick: () => console.log("clicked") },
            ]}
          />
        </div>
        <div className="text-sm lg:text-base text-brand-bistre space-x-2">
          {comparedAtPrice !== price && Number(comparedAtPrice) > Number(price) && (
            <span className="font-normal text-neutrals-secondaryGrey line-through">
              ${Number(comparedAtPrice).toFixed(2)}
            </span>
          )}
          <span className="font-medium">${Number(price).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};
